<template>
  <v-dialog
      v-model="dialog"
      width="1000px"
      transition="dialog-bottom-transition"
      :retain-focus="false"
      content-class="conversation-dialog"
  >
    <v-card elevation="8" rounded="lg">
      <v-toolbar color="primary" dark flat density="comfortable">
        <v-toolbar-title class="text-h6 font-weight-medium">
          <v-icon start icon="mdi-forum-outline" class="me-2"></v-icon>
          {{ t('focusgroupdialog.conversationHistory') }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn
            icon="mdi-close"
            @click="closeDialog"
            variant="text"
            density="comfortable"
            class="transition-swing"
        ></v-btn>
      </v-toolbar>

      <v-divider></v-divider>

      <v-card-text class="pa-0">
        <v-container fluid class="pa-4">
          <div class="conversation-container pa-2" ref="conversationContainer">
            <template v-for="(history, index) in renderedHistories" :key="index">
              <div v-if="history.stepHeader" class="step-header rounded pa-3 mb-4">
                <div class="d-flex align-center flex-wrap ga-2">
                  <v-icon icon="mdi-file-tree-outline" size="18" color="primary"></v-icon>
                  <span class="text-subtitle-2 font-weight-bold">
                    {{ t('focusgroupdialog.step') }} {{ history.stepHeader.order }} —
                    {{ history.stepHeader.step_name }}
                  </span>

                  <v-spacer></v-spacer>

                  <v-chip
                      v-if="hasAttachments && stepShowsAttachment(history.stepHeader)"
                      size="x-small"
                      variant="tonal"
                      color="primary"
                      prepend-icon="mdi-paperclip"
                  >
                    {{ t('focusgroupdialog.materialsShown') }}
                  </v-chip>
                  <v-chip size="x-small" variant="tonal" color="primary">
                    {{ history.stepHeader.rounds_used }} {{ t('focusgroupdialog.rounds') }}
                  </v-chip>
                  <v-chip
                      v-if="completedByLabel(history.stepHeader)"
                      size="x-small"
                      variant="tonal"
                      color="grey-darken-1"
                  >
                    {{ completedByLabel(history.stepHeader) }}
                  </v-chip>
                </div>
              </div>

              <div
                  class="message-entry rounded pa-4 mb-4"
                  :class="{'interviewer-message': isModerator(history.persona_name)}"
              >
                <div class="d-flex align-center mb-2">
                  <v-avatar :color="getPersonaColor(history.persona_name)" size="32" class="me-3">
                    <span class="text-white">{{ history.persona_name.charAt(0).toUpperCase() }}</span>
                  </v-avatar>

                  <div class="persona-name text-subtitle-1 font-weight-bold">
                    {{ formatPersonaName(history.persona_name) }}
                  </div>

                  <v-spacer></v-spacer>

                  <v-chip
                      v-if="showTimestamps && history.timestamp"
                      size="small"
                      variant="flat"
                      color="grey"
                      class="text-caption"
                  >
                    {{ formatTimestamp(history.timestamp) }}
                  </v-chip>
                </div>

                <div
                    v-if="isModerator(history.persona_name) && history.image_id && getImageUrl(history.image_id)"
                    class="message-image-attachment mb-4"
                >
                  <v-img
                      :src="getImageUrl(history.image_id)"
                      max-height="300"
                      contain
                      class="rounded-lg elevation-4 mx-auto"
                      @error="handleImageError(history.image_id)"
                      crossorigin="anonymous"
                  >
                    <template v-slot:placeholder>
                      <div class="d-flex align-center justify-center fill-height">
                        <v-progress-circular indeterminate color="grey-lighten-4"></v-progress-circular>
                      </div>
                    </template>
                  </v-img>
                  <div class="text-caption mt-2 text-medium-emphasis text-center">
                    {{ t('focusgroupdialog.attachment') }}: {{ history.image_id }}

                    <v-tooltip :text="$t('focusgroupdialog.downloadImagePng')">
                      <template v-slot:activator="{ props }">
                        <v-icon
                            v-bind="props"
                            class="ms-2 cursor-pointer icon-size-large"
                            color="primary"
                            @click="downloadImage(getImageUrl(history.image_id), history.image_id)"
                        >
                          mdi-download
                        </v-icon>
                      </template>
                    </v-tooltip>
                  </div>
                </div>

                <div
                    class="message-content ml-8 pl-2"
                >
                  <div v-html="parseMessageWithPersonaColors(history.message)"></div>

                  <v-chip
                      v-if="isModerator(history.persona_name) && history.pdf_page_number"
                      color="info"
                      variant="tonal"
                      size="small"
                      class="mt-3"
                      density="comfortable"
                  >
                    <v-icon start icon="mdi-file-pdf-box" size="16"></v-icon>
                    {{ t('focusgroupdialog.pdfPage') }}: {{ history.pdf_page_number }}
                  </v-chip>
                </div>

              </div>

              <v-divider
                  v-if="index < renderedHistories.length - 1"
                  class="mb-4"
              ></v-divider>
            </template>

            <v-fade-transition>
              <div v-if="filteredHistories.length === 0 && searchQuery" class="text-center pa-8">
                <v-icon size="large" color="grey-lighten-1">mdi-text-search</v-icon>
                <div class="text-body-1 mt-2">{{ t('focusgroupdialog.noMessagesFoundMatching') }} "{{ searchQuery }}"</div>
              </div>
            </v-fade-transition>
          </div>
        </v-container>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-btn
            prepend-icon="mdi-content-copy"
            color="primary"
            variant="tonal"
            @click="copyConversationToClipboard"
            :loading="isCopying"
        >
          {{ t('focusgroupdialog.copyConversation') }}
        </v-btn>

        <v-btn
            prepend-icon="mdi-download"
            color="primary"
            variant="tonal"
            @click="downloadConversation"
            class="ms-2"
            :loading="isDownloading"
        >
          {{ t('generic.download') }}
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn
            color="grey"
            variant="text"
            @click="closeDialog"
        >
          {{ t('generic.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

</template>

<script>
import { marked } from 'marked';
import jsPDF from 'jspdf';
import html2canvas from "html2canvas";
import { useI18n } from "vue-i18n";
import { formatDateTime } from '@/utils/dateFormat';
import { stepShowsAttachment } from '@/utils/meetingTemplateSteps';

// Reserved speaker names for the moderator: question-driven runs use
// 'interviewer', template-based runs use 'facilitator'.
const MODERATOR_NAMES = ['interviewer', 'facilitator'];

export default {
  name: 'ConversationDialog',
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    histories: {
      type: Array,
      required: true,
      default: () => []
    },
    // Executed steps of a template-based run: [{step_id, step_name, order,
    // rounds_used, completed_by, ...}]. Empty for question-driven results.
    steps: {
      type: Array,
      default: () => []
    },
    // The run had stimulus materials: mark the steps that showed them.
    hasAttachments: {
      type: Boolean,
      default: false
    },
    showTimestamps: {
      type: Boolean,
      default: false
    },
    images_url: {
      type: Array,
      default: () => []
    }
  },
  setup() {
    const { t } = useI18n();

    return {
    t
    };
  },
  data() {
    return {
      dialog: this.showDialog,
      searchQuery: '',
      isCopying: false,
      isDownloading: false,
      personaColorMap: {},
      vibrantColors: {
        'moderator': '#263238',
        'default': [
          '#1976D2',
          '#7B1FA2',
          '#388E3C',
          '#D32F2F',
          '#F57C00',
          '#0097A7',
          '#C2185B',
          '#00796B',
          '#FBC02D'
        ]
      },
      colorIndex: 0,
      showImageOverlay: false, // Mantenuto, ma non usato
      fullSizeImageUrl: '',    // Mantenuto, ma non usato
    };
  },
  computed: {
    filteredHistories() {
      if (!this.searchQuery.trim()) {
        return this.histories;
      }

      const query = this.searchQuery.toLowerCase();
      return this.histories.filter(history =>
          history.message.toLowerCase().includes(query) ||
          history.persona_name.toLowerCase().includes(query)
      );
    },
    // Decorates each entry with the step record to render a header for, set only
    // on the first entry of each step. Template runs carry a `step_id` on every
    // history entry and the entries are contiguous per step; question-driven
    // entries have none, so `stepHeader` stays null and rendering is unchanged.
    renderedHistories() {
      const stepById = new Map(this.steps.map(step => [step.step_id, step]));
      let previousStepId = null;

      return this.filteredHistories.map(history => {
        const isNewStep = history.step_id && history.step_id !== previousStepId;
        previousStepId = history.step_id ?? null;

        return {
          ...history,
          stepHeader: isNewStep ? (stepById.get(history.step_id) ?? null) : null,
        };
      });
    }
  },
  watch: {
    showDialog(val) {
      this.dialog = val;
    },
    histories: {
      handler: function() {
        this.initializePersonaColors();
      },
      deep: true,
      immediate: true
    },
    dialog(val) {
      this.$emit('update:showDialog', val);
    },
  },
  mounted() {
    this.initializePersonaColors();
    console.log("focusgroupdialog mounted");
    console.log("Received images_url:", this.images_url);
    console.log("Received histories:", this.histories);
  },
  methods: {
    initializePersonaColors() {
      this.personaColorMap = {};
      this.colorIndex = 0;

      if (this.histories && this.histories.length > 0) {
        const uniquePersonaNames = [...new Set(this.histories.map(h => h.persona_name))];

        MODERATOR_NAMES.forEach(name => {
          this.personaColorMap[name] = this.vibrantColors['moderator'];
        });

        uniquePersonaNames.forEach(personaName => {
          if (!this.isModerator(personaName) && !this.personaColorMap[personaName]) {
            this.personaColorMap[personaName] = this.vibrantColors.default[this.colorIndex % this.vibrantColors.default.length];
            this.colorIndex++;
          }
        });
      }
    },
    isModerator(personaName) {
      return MODERATOR_NAMES.includes(personaName);
    },
    stepShowsAttachment,
    completedByLabel(step) {
      const labels = {
        facilitator: 'focusgroupdialog.completedByFacilitator',
        round_cap: 'focusgroupdialog.completedByRoundCap',
        judge_error: 'focusgroupdialog.completedByJudgeError',
        fixed_rounds: 'focusgroupdialog.completedByFixedRounds',
      };
      const key = labels[step?.completed_by];
      return key ? this.t(key) : (step?.completed_by ?? '');
    },
    getPersonaColor(personaName) {
      return this.personaColorMap[personaName] || this.vibrantColors.default[0];
    },
    formatPersonaName(name) {
      // 'interviewer' is a stored role identifier (also drives the message
      // styling / colour map) — keep it as-is internally, only localise the
      // label shown to the user.
      if (name === 'interviewer') {
        return this.t('focusgroupdialog.interviewer');
      }
      return name
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    },
    formatTimestamp(timestamp) {
      return formatDateTime(timestamp, this.$i18n.locale, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: undefined,
        year: undefined,
      });
    },
    convertMarkdown(text) {
      if (!text) return '';
      return marked.parse(text);
    },
    parseMessageWithPersonaColors(message) {
      if (!message || message === "<no response>") {
        return `<span class="no-response">${this.t('focusgroupdialog.noResponseProvided')}</span>`;
      }

      let parsedMessage = this.convertMarkdown(message);

      for (const personaName in this.personaColorMap) {
        const color = this.getPersonaColor(personaName);
        const nameParts = personaName.trim().split(/\s+/).filter(p => p.length > 1);
        const patterns = [personaName, ...nameParts.filter(p => p !== personaName)];
        for (const pattern of patterns) {
          const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`((?<!\\p{L})${escaped}'?s?(?!\\p{L}))`, 'giu');
          parsedMessage = parsedMessage.replace(
              regex,
              `<span class="persona-mention" style="color:${color}; font-weight:500;">$1</span>`
          );
        }
      }

      return parsedMessage;
    },
    getImageUrl(fileId) {
      if (!fileId || !Array.isArray(this.images_url)) {
        console.log("getImageUrl: no fileId or images_url", { fileId, images_url: this.images_url });
        return null;
      }

      console.log("Looking for image:", fileId);
      console.log("Available images:", this.images_url);

      // 1) Match diretto esatto
      let entry = this.images_url.find(img => img.file_id === fileId);
      if (entry) {
        console.log("Found exact match:", entry.url);
        return entry.url;
      }

      // 2) Normalizza al solo nome file (senza path)
      const fname = fileId.toString().split('/').pop().split('\\').pop();
      if (fname && fname !== fileId) {
        entry = this.images_url.find(img => {
          const imgFileName = img.file_id.split('/').pop().split('\\').pop();
          return imgFileName === fname;
        });
        if (entry) {
          console.log("Found by filename:", entry.url);
          return entry.url;
        }
      }

      // 3) Match parziale (contiene)
      entry = this.images_url.find(img =>
          img.file_id.includes(fileId) || fileId.includes(img.file_id)
      );
      if (entry) {
        console.log("Found partial match:", entry.url);
        return entry.url;
      }

      console.log("No image found for:", fileId);
      return null;
    },
    handleImageError(imageId) {
      console.error("Failed to load image:", imageId);
      console.log("Available images:", this.images_url);
      this.$emit('notify', {
        type: 'error',
        message: `Failed to load image: ${imageId}`
      });
    },
    closeDialog() {
      this.dialog = false;
      this.searchQuery = '';
    },
    scrollToBottom() {
      const container = this.$refs.conversationContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    async copyConversationToClipboard() {
      this.isCopying = true;

      try {
        const text = this.renderedHistories
            .map(h => {
              const line = `${this.formatPersonaName(h.persona_name)}: ${h.message.replace(/<[^>]*>/g, '')}`;
              if (!h.stepHeader) return line;

              const step = h.stepHeader;
              const header = `=== ${this.t('focusgroupdialog.step')} ${step.order} — ${step.step_name} `
                  + `(${step.rounds_used} ${this.t('focusgroupdialog.rounds')}, ${this.completedByLabel(step)}) ===`;
              return `${header}\n\n${line}`;
            })
            .join('\n\n');

        await navigator.clipboard.writeText(text);

        this.$emit('notify', {
          type: 'success',
          message: 'Conversation copied to clipboard'
        });
      } catch (error) {
        console.error('Failed to copy:', error);
        this.$emit('notify', {
          type: 'error',
          message: 'Failed to copy conversation'
        });
      } finally {
        this.isCopying = false;
      }
    },
    async downloadConversation() {
      this.isDownloading = true;

      const element = this.$refs.conversationContainer;

      const clone = element.cloneNode(true);
      clone.style.maxHeight = "none";
      clone.style.overflow = "visible";
      clone.style.height = "auto";
      clone.style.width = element.offsetWidth + "px";
      clone.classList.add("pdf-export");

      const hiddenContainer = document.createElement("div");
      hiddenContainer.style.position = "absolute";
      hiddenContainer.style.left = "-9999px";
      hiddenContainer.appendChild(clone);
      document.body.appendChild(hiddenContainer);

      // Ritardo di 3 secondi (3000ms) per garantire il caricamento dell'immagine
      await new Promise(resolve => setTimeout(resolve, 3000));

      const scale = 1.0;
      let canvas;

      try {
        canvas = await html2canvas(clone, {
          scale,
          useCORS: true,
          backgroundColor: "#ffffff",
        });
      } catch (err) {
        console.error("html2canvas error (image loading/CORS issue):", err);
        this.$emit('notify', {
          type: 'error',
          message: 'Failed to generate PDF. Check if image links support CORS.'
        });
        document.body.removeChild(hiddenContainer);
        this.isDownloading = false;
        return;
      } finally {
        this.isDownloading = false; // Reset loading state
      }

      document.body.removeChild(hiddenContainer);

      const imgData = canvas.toDataURL("image/png");
      const pdfWidth = 595.28;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      const pdf = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: [pdfWidth, pdfHeight],
      });

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.setFontSize(12);

      pdf.save(`conversation-${new Date().toISOString().slice(0, 10)}.pdf`);
    },

    // RIMOSSO openImageOverlay()

    async downloadImage(imageUrl, imageId) {
      if (!imageUrl) return;

      try {
        const response = await fetch(imageUrl, {
          mode: 'cors'
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        // 1. Ottieni un nome file base (dall'ID o dall'URL)
        let baseFileName = imageId ? imageId.split('/').pop().split('\\').pop() : 'downloaded_image';
        // 2. Rimuovi l'estensione esistente se presente
        baseFileName = baseFileName.replace(/\.[^/.]+$/, "");
        // 3. Aggiungi l'estensione .png
        a.download = `${baseFileName}.png`;

        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        this.$emit('notify', {
          type: 'success',
          message: `Image '${baseFileName}.png' downloaded.`
        });
      } catch (error) {
        console.error("Error downloading image:", error);
        this.$emit('notify', {
          type: 'error',
          message: `Failed to download image '${imageId}'. Check console for CORS issues.`
        });
      }
    },
  }
};
</script>

<style scoped>
.conversation-dialog {
  border-radius: 8px;
  overflow: hidden;
}

.conversation-container {
  max-height: 60vh;
  overflow-y: auto;
  scrollbar-width: thin;
}

.message-entry {
  transition: background-color 0.2s ease;
  background-color: #f8f9fa;
}

.message-entry:hover {
  background-color: #f1f3f4;
}

.interviewer-message {
  background-color: #eceff1;
}

.step-header {
  background-color: #e3f2fd;
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.message-content {
  line-height: 1.6;
}

.message-image-attachment {
  position: relative;
  max-width: 80%;
  margin: 12px auto;
  border: 1px solid #cfd8dc;
  padding: 8px;
  background-color: #ffffff;
  border-radius: 8px;
}

/* Stile per rendere le icone più grandi e visibili (solo per l'icona download ora) */
.icon-size-large {
  font-size: 28px !important;
  width: 28px !important;
  height: 28px !important;
}

.message-content :deep(code) {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 2px 4px;
  border-radius: 4px;
}

.message-content :deep(pre) {
  background-color: #263238;
  color: #ffffff;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
}

.message-content :deep(a) {
  color: #1976D2;
  text-decoration: none;
}

.message-content :deep(a:hover) {
  text-decoration: underline;
}

.message-content :deep(blockquote) {
  border-left: 4px solid #9e9e9e;
  margin-left: 0;
  padding-left: 16px;
  color: #616161;
}

.message-content :deep(.no-response) {
  color: #9e9e9e;
  font-style: italic;
}

.cursor-pointer {
  cursor: pointer;
}

/* RIMOSSI gli stili relativi all'overlay */
</style>